/**
 * Die serverseitige Gate-Entscheidung im Vokabular des Entwurfs.
 *
 * Die Oberfläche entscheidet nicht selbst, ob ein Merge erlaubt ist. Sie
 * übersetzt nur `modus` und `entscheidung` in ein Wort und einen vorhandenen
 * Stand. So bleiben MR-Zeile und Detailansicht identisch.
 *
 * Sie liest dabei ausdrücklich **nicht** `haetteBlockiert`. Das Feld ist
 * serverseitig `entscheidung !== "bestanden"` und wirft damit „ein Signal
 * fehlt noch" mit „ein Signal ist gerissen" zusammen. Für den Merge ist das
 * richtig — beides hätte ihn aufgehalten. Für die Farbe ist es falsch: Am
 * 21.08.2026 waren dadurch sämtliche Merge Requests beider Projekte rot,
 * obwohl nur zwei davon eine gescheiterte Pipeline hatten. Die übrigen
 * warteten auf ein Auto-Q&A-Signal, das nie ausgewählt worden war.
 */

import type {
  QualitaetsGateEntscheidung,
  QualitaetsGateModus,
  QualitaetsGateSchwelle,
} from "@cubic/shared";
import type { Stand } from "@/lib/entwurf-zustaende";

export type QualitaetsGateKurz = {
  modus: QualitaetsGateModus;
  entscheidung: QualitaetsGateEntscheidung;
  darfMergen: boolean;
  haetteBlockiert: boolean;
};

export type QualitaetsGateAnzeige = {
  label: string;
  stand: Stand;
  erklaerung: string;
};

export const QUALITAETS_GATE_MODUS_TEXT: Readonly<
  Record<QualitaetsGateModus, string>
> = {
  aus: "Aus",
  schatten: "Beobachtung",
  blockierend: "Blockierend",
};

/** Beschriftung und Erklärung einer Entscheidung in einem der beiden Modi. */
type Wortlaut = { label: string; erklaerung: string };

/**
 * Der Entwurfsstand einer Entscheidung — in beiden bewertenden Modi derselbe.
 *
 * Die Beobachtung zeigt, was ein scharfes Gate täte; also muss sie denselben
 * Punkt zeichnen. Hier stand vorher nur „blockiert oder bestanden", und jedes
 * bloß ausstehende Signal bekam die Warnfarbe eines gerissenen.
 */
function standDerEntscheidung(
  entscheidung: QualitaetsGateEntscheidung,
): Stand {
  switch (entscheidung) {
    case "bestanden":
      return "ausgefuehrt";
    case "ausstehend":
      return "wartet";
    case "blockiert":
      return "blockiert";
    case "nicht_bewertbar":
      return "gescheitert";
    case "uebersteuert":
      return "abgebrochen";
  }
}

/** Im blockierenden Betrieb hält das Gate den Merge tatsächlich auf. */
const BLOCKIEREND: Readonly<Record<QualitaetsGateEntscheidung, Wortlaut>> = {
  bestanden: {
    label: "Gate bestanden",
    erklaerung: "Alle erforderlichen Signale sind aktuell und bestanden.",
  },
  ausstehend: {
    label: "Gate ausstehend",
    erklaerung: "Mindestens ein erforderliches Signal fehlt oder läuft noch.",
  },
  blockiert: {
    label: "Gate blockiert",
    erklaerung: "Mindestens ein erforderliches Signal hält den Merge auf.",
  },
  nicht_bewertbar: {
    label: "Nicht bewertbar",
    erklaerung:
      "Mindestens ein erforderliches Signal konnte nicht verlässlich bewertet werden.",
  },
  uebersteuert: {
    label: "Gate übersteuert",
    erklaerung:
      "Die ursprüngliche Gate-Entscheidung wurde für diese Prüfung begründet übersteuert.",
  },
};

/**
 * In der Beobachtung bewertet dasselbe Gate, ohne den Merge zu sperren.
 *
 * Bis zum 21.08.2026 hieß dieser Modus an der Oberfläche „Schattenbetrieb"
 * und die Marke las sich „Schatten · hätte blockiert" — ein Wort, das nur
 * versteht, wer die Entscheidung ohnehin kennt. Der Modusname in Datenbank
 * und Typen bleibt `schatten`; ersetzt ist allein das Wort, das jemand liest.
 */
const BEOBACHTUNG: Readonly<Record<QualitaetsGateEntscheidung, Wortlaut>> = {
  bestanden: {
    label: "Beobachtung · bestanden",
    erklaerung:
      "Die gegenwärtigen Signale würden ein blockierendes Gate bestehen.",
  },
  ausstehend: {
    label: "Beobachtung · ausstehend",
    erklaerung:
      "Mindestens ein erforderliches Signal fehlt oder läuft noch. Ein blockierendes Gate würde darauf warten; hier hält nichts den Merge auf.",
  },
  blockiert: {
    label: "Beobachtung · hätte blockiert",
    erklaerung:
      "Im blockierenden Betrieb wäre dieser Merge nicht erlaubt. Solange das Gate nur beobachtet, bleibt er möglich.",
  },
  nicht_bewertbar: {
    label: "Beobachtung · nicht bewertbar",
    erklaerung:
      "Mindestens ein erforderliches Signal konnte nicht verlässlich bewertet werden. Solange das Gate nur beobachtet, hält das keinen Merge auf.",
  },
  uebersteuert: {
    label: "Beobachtung · übersteuert",
    erklaerung:
      "Die ursprüngliche Gate-Entscheidung wurde begründet übersteuert. Solange das Gate nur beobachtet, hätte sie den Merge ohnehin nicht aufgehalten.",
  },
};

export function qualitaetsGateAnzeige(
  gate: QualitaetsGateKurz,
): QualitaetsGateAnzeige {
  if (gate.modus === "aus") {
    return {
      label: "Gate aus",
      stand: "pausiert",
      erklaerung: "Das Qualitäts-Gate bewertet diesen Merge Request nicht.",
    };
  }
  const wortlaut =
    gate.modus === "schatten"
      ? BEOBACHTUNG[gate.entscheidung]
      : BLOCKIEREND[gate.entscheidung];
  return { ...wortlaut, stand: standDerEntscheidung(gate.entscheidung) };
}

export function qualitaetsGateLaeuft(
  gruende: readonly { code: string }[],
): boolean {
  return gruende.some(
    (grund) =>
      grund.code === "pipeline_laeuft" || grund.code === "auto_qa_laeuft",
  );
}

export type QualitaetsGateRegelAuswahl = {
  modus: QualitaetsGateModus;
  autoQaErforderlich: boolean;
  schwereGrenze: QualitaetsGateSchwelle;
  automationIds: readonly string[];
};

/** Dieselben zwei Formregeln, die der Server an seiner Grenze durchsetzt. */
export function qualitaetsGateRegelFehler(
  regel: QualitaetsGateRegelAuswahl,
): string | null {
  if (regel.automationIds.length > 20) {
    return "Für ein Quality Gate können höchstens 20 Auto-Q&A-Automationen ausgewählt werden.";
  }
  // Ein Pflichtsignal ohne ausgewählte Automation ist per Konstruktion nie
  // erfüllt: Die Bewertung meldet `auto_qa_fehlt`, und der Merge Request
  // bleibt für immer ausstehend. Das galt bis zum 21.08.2026 nur als Fehler,
  // wenn das Gate blockierte — in der Beobachtung war derselbe Zustand
  // erlaubt und stand obendrein in der Vorgabe. `aus` bleibt ausgenommen:
  // Dort bewertet das Gate nichts, die Auswahl liegt bloß brach.
  if (
    regel.modus !== "aus" &&
    regel.autoQaErforderlich &&
    regel.automationIds.length === 0
  ) {
    return "Wähle mindestens eine Auto-Q&A-Automation aus oder schalte Auto Q&A als Pflichtsignal ab — sonst bleibt jeder Merge Request dauerhaft ausstehend.";
  }
  return null;
}
